import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, GraduationCap, Award, TrendingUp, Landmark } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { MarketingNav, MarketingFooter } from '../components/MarketingLayout';
import { ProofBar } from '../components/ProofBar';
import { MentorStrip } from '../components/MentorStrip';
import { analytics } from '../hooks/useAnalytics';

const OUTCOME_CATEGORIES = [
  {
    icon: GraduationCap,
    title: 'Ivy League & Ivy Plus',
    headline: '200+ placements',
    desc: 'Admits across Harvard, Yale, Princeton, Columbia, Penn, Brown, Cornell, and Dartmouth — plus Ivy Plus peers including Stanford, MIT, Duke, and the University of Chicago.',
  },
  {
    icon: Award,
    title: 'Scholarships',
    headline: '$38M+ secured',
    desc: 'Cumulative merit and need-based awards reported by client families — including full-tuition and full-ride offers at top-30 institutions.',
  },
  {
    icon: TrendingUp,
    title: 'Transfer Outcomes',
    headline: 'CC → Top 25',
    desc: 'Community college and lateral transfer students placed at Michigan, UVA, and other top public and private institutions through GPA-first transfer strategy.',
  },
  {
    icon: Landmark,
    title: 'Top Public Flagships',
    headline: 'Berkeley to UNC',
    desc: 'For families optimizing cost and outcome, placements at flagship honors programs with significant merit support.',
  },
];

const CASE_STUDIES = [
  {
    tag: 'Narrative Repositioning',
    title: 'The 3.4 GPA reframe',
    before: 'Junior with a 3.4 GPA, strong work ethic, no obvious hook, and a scattered activities list.',
    strategy: 'Rebuilt the application around applied entrepreneurship — a small business the student ran was moved from the bottom of the activities list to the center of the narrative. Essays, recommenders, and interviews were aligned to one story.',
    outcome: 'Admitted to USC, Boston University, and Northeastern.',
  },
  {
    tag: 'Signal Development',
    title: 'The quiet researcher',
    before: 'Exceptional STEM student with top scores but a profile indistinguishable from thousands of similar applicants.',
    strategy: 'Paired with an Ivy student mentor in the intended major and a research-track career mentor. Built one deep, documented independent research thread and rewrote supplementals around intellectual direction.',
    outcome: "Admitted to an Ivy League institution, Class of '28, with merit support.",
  },
  {
    tag: 'Transfer Strategy',
    title: 'The transfer route',
    before: 'Community college student with no counseling support and no roadmap for the transfer process.',
    strategy: 'GPA-first sequencing, a transfer-specific school list, and essays that framed the community college start as a deliberate, disciplined decision rather than a fallback.',
    outcome: 'Admitted to the University of Michigan and UVA as a transfer.',
  },
  {
    tag: 'Scholarship Optimization',
    title: 'The scholarship-first family',
    before: 'Strong student; family unwilling to pay full sticker price and unsure how to weigh prestige against cost.',
    strategy: 'Rebuilt the school list around documented merit-aid behavior, layered in honors-college applications, and sequenced early applications to maximize negotiating position.',
    outcome: 'Over $240K in cumulative merit offers; enrolled at a top-30 institution on a near-full scholarship.',
  },
];

export default function ResultsPage() {
  useEffect(() => {
    analytics.resultsPageView();
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="results-page">
      <SEO
        title="Results & Outcomes — 200+ Ivy Placements, $38M+ Scholarships"
        description="200+ Ivy League placements and $38M+ in scholarships secured — reported responsibly with anonymized case studies and a clear methodology note on how every number is counted."
        keywords="ivy league admissions results, college admissions outcomes, ivy league acceptance stories, admissions case studies"
      />
      <MarketingNav />

      {/* Hero */}
      <section className="pt-16 bg-[#0F392B] text-white">
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-4">Results & Outcomes</p>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.1] mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
            Outcomes, reported <span className="italic text-[#D4AF37]">responsibly.</span>
          </h1>
          <p className="text-base md:text-lg text-white/70 max-w-xl mx-auto leading-relaxed">
            No hype, no inflated claims. Placements, scholarship totals, and anonymized case studies — with a clear note on how each number is counted.
          </p>
        </div>
      </section>

      <ProofBar location="results-hero" />

      {/* Outcome Categories */}
      <section className="py-16 md:py-20" data-testid="results-categories">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {OUTCOME_CATEGORIES.map((c, i) => (
            <div key={i} className="p-6 rounded-2xl bg-[#F9F9F7] border border-[#E2E2E2] hover:shadow-lg transition-shadow" data-testid={`results-category-${i}`}>
              <c.icon className="h-5 w-5 text-[#0F392B] mb-4" strokeWidth={1.5} />
              <p className="text-[11px] uppercase tracking-widest text-[#D4AF37] font-medium mb-1">{c.title}</p>
              <p className="text-2xl font-semibold text-[#0F392B] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{c.headline}</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Mentor Strip */}
      <MentorStrip
        bg="dark"
        testid="results-mentor-strip"
        headline={"The mentors behind these\u00A0"}
        headlineItalic="outcomes."
        subtext="Every result on this page is the product of a client working alongside a current Ivy League student mentor. Anonymized snapshots of our active bench — matched by major, target schools, and background."
      />

      {/* Case Studies */}
      <section className="py-16 md:py-20 bg-[#F9F9F7] border-y border-[#E2E2E2]" data-testid="results-case-studies">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-3">Selected Case Studies</p>
            <h2 className="text-2xl md:text-3xl font-medium tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              How strategy changes outcomes
            </h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-lg mx-auto">All case studies are anonymized; identifying details are altered to protect client privacy.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {CASE_STUDIES.map((cs, i) => (
              <div key={i} className="bg-white rounded-2xl border border-[#E2E2E2] p-7 hover:shadow-lg transition-shadow" data-testid={`case-study-${i}`}>
                <p className="text-[10px] font-medium text-[#D4AF37] uppercase tracking-widest mb-2">{cs.tag}</p>
                <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{cs.title}</h3>
                <div className="space-y-3 text-xs leading-relaxed">
                  <div>
                    <p className="font-semibold text-foreground mb-0.5">Starting point</p>
                    <p className="text-muted-foreground">{cs.before}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground mb-0.5">Strategy</p>
                    <p className="text-muted-foreground">{cs.strategy}</p>
                  </div>
                  <div className="pt-2 border-t border-[#E2E2E2]">
                    <p className="font-semibold text-[#0F392B]">{cs.outcome}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="py-16" data-testid="results-methodology">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-[#E2E2E2] bg-white p-8 shadow-sm">
            <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-3">How We Report Outcomes</p>
            <ul className="space-y-3 text-sm text-muted-foreground leading-relaxed list-none">
              <li><span className="text-foreground font-medium">Placements</span> are counted across firm engagements and our mentor network since founding, and include both consulting clients and platform families who reported results.</li>
              <li><span className="text-foreground font-medium">Scholarship totals</span> are cumulative merit and need-based offers reported by client families — offers, not necessarily amounts accepted.</li>
              <li><span className="text-foreground font-medium">Case studies</span> are anonymized and lightly altered to protect privacy while preserving the strategic substance.</li>
              <li><span className="text-foreground font-medium">No guarantees.</span> Admissions outcomes depend on factors no firm controls. We report what our process has produced — we never promise what it will.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#0F392B]" data-testid="results-cta-section">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Discuss what these results mean for your student.
          </h2>
          <p className="text-sm text-white/60 mb-8 max-w-md mx-auto">
            A confidential, no-commitment conversation about your profile, your goals, and the right level of engagement.
          </p>
          <Link to="/consultation" onClick={() => analytics.ctaStrategyCall('results-page')}>
            <Button size="lg" className="bg-[#D4AF37] text-[#0F392B] hover:bg-[#D4AF37]/90 h-12 px-10 font-semibold shadow-xl" data-testid="results-strategy-call-btn">
              Book a Strategy Call <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
